import { KeycloakInitOptions } from 'keycloak-js';
import { isNotProductionMode } from '@dxb-web-ui-framework/common/domain/Environment/EnvironmentService';
import { getSameHostEndpoint } from '@dxb-web-ui-framework/common/domain/ServiceDiscovery/ServiceDiscoveryService';
import brandLogo from '../assets/logo.png';
import EcommbxLogoDesktop from '../assets/static/images/ic-logo-ecommbx.svg';
import EcommbxLogoMobile from '../assets/static/images/ic-logo-mobile.svg';
import heroImage from '../assets/static/images/dashboard/newDragonLogo.svg';
import { HeroBannerTypes, SpiltScreenTypes } from '../../../packages/content-composer/src/components/Layouts/types';
import { getRoutePath } from '../../../packages/content-composer/src/components/Routes/routePaths';
const baseUrl = isNotProductionMode() ? '/ecommbx/secure-web' : '/secure-web';

const brandedAppConfig = {
  baseName: baseUrl,
  keycloakInitOptions: (url: string) =>
    ({
      onLoad: 'login-required',
      flow: 'standard',
      pkceMethod: 'S256',
      redirectUri: getSameHostEndpoint(url),
    } as KeycloakInitOptions),
  featureFlags: {
    activationFlowEnabled: false,
  },
  timerExpirationHandler: 'idle-timer-hook',
  idleTimerEnabled: true,
  idleTimeout: 1000 * 60 * 15, // 15min
  layout: {
    logo: brandLogo,
    logoAlt: 'ecommbx-logo',
  },
  logoDesktop: EcommbxLogoDesktop,
  logoMobile: EcommbxLogoMobile,
  dashboardPage: {
    title: 'brandedAppConfig.dashboardPage.Title',
    layout: {
      type: 'HeroBannerLayout',
      heroMoto: 'brandedAppConfig.dashboardPage.Layout.HeroMoto',
      heroSlogan: 'brandedAppConfig.dashboardPage.Layout.HeroSlogan',
      heroImage,
      containerType: HeroBannerTypes.DASHBOARD,
      dataTestId: 'dashboard-page',
    },
  },
  accountsPage: {
    title: 'brandedAppConfig.accountsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.accountsPage.Layout.Label',
      dataTestId: 'accounts-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  accountDetailsPage: {
    title: 'brandedAppConfig.accountDetailsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.accountDetailsPage.Layout.Label',
      dataTestId: 'account-details-page',
      backBtnUrl: ({ customerId, accountId }: { customerId: string; accountId: string }) =>
        getRoutePath({
          routeKey: 'accounts',
          customerId,
          accountId,
        }),
    },
  },
  communicationPreferencesPage: {
    title: 'brandedAppConfig.communicationPreferencesPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.communicationPreferencesPage.Layout.Label',
      dataTestId: 'communication-preferences-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  termsCondition: {
    title: 'brandedAppConfig.termsCondition.Title',
    layout: {
      type: 'SplitScreenLayout',
      moto: 'brandedAppConfig.termsCondition.Moto',
      dataTestId: 'ecommbx-termsCondition-page',
      leftContainerType: SpiltScreenTypes.TERMS_CONDITION,
    },
  },
  gdprPage: {
    title: 'brandedAppConfig.gdprPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.gdprPage.Layout.Label',
      dataTestId: 'gdpr-page',
    },
  },
  deviceManagementPage: {
    title: 'brandedAppConfig.deviceManagementPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.deviceManagementPage.Layout.Label',
      dataTestId: 'device-management',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  errorPage404: {
    title: 'brandedAppConfig.errorPage404.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.errorPage404.Layout.Label',
      dataTestId: '404-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  transactionHistoryPage: {
    title: 'brandedAppConfig.transactionHistoryPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.transactionHistoryPage.Layout.Label',
      dataTestId: 'transaction-history-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  transactionDetailsPage: {
    title: 'brandedAppConfig.transactionDetailsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.transactionDetailsPage.Layout.Label',
      dataTestId: 'transaction-details-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'transactions',
          customerId,
        }),
    },
  },
  accountExtraitTransactionsPage: {
    title: 'brandedAppConfig.accountExtraitTransactionsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.accountExtraitTransactionsPage.Layout.Label',
      dataTestId: 'account-transactions-page',
      backBtnUrl: ({ customerId, accountId }: { customerId: string; accountId: string }) =>
        getRoutePath({
          routeKey: 'account-details',
          customerId,
          accountId,
        }),
    },
  },
  accountExtraitTransactionDetailsPage: {
    title: 'brandedAppConfig.accountExtraitTransactionsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.accountExtraitTransactionsPage.Layout.Label',
      dataTestId: 'account-transaction-details-page',
      backBtnUrl: ({ customerId, accountId }: { customerId: string; accountId: string }) =>
        getRoutePath({
          routeKey: 'accountExtraitTransactionsPage',
          customerId,
          accountId,
        }),
    },
  },
  cardTransactionDetailsPage: {
    title: 'Card Transaction Details',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.cardTransactionDetailsPage.Layout.Label',
      dataTestId: 'card-transaction-details-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'cards',
          customerId,
        }),
    },
  },
  userProfilePage: {
    title: 'brandedAppConfig.userProfilePage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.userProfilePage.Layout.Label',
      dataTestId: 'user-profile-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  securityPreferencesPage: {
    title: 'brandedAppConfig.securityPreferencesPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.securityPreferencesPage.Layout.Label',
      dataTestId: 'security-preferences-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  securityPreferencesChangePasswordPage: {
    title: 'brandedAppConfig.securityPreferencesChangePasswordPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.securityPreferencesChangePasswordPage.Layout.Label',
      dataTestId: 'security-preferences-change-password-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'security-preferences',
          customerId,
        }),
    },
  },
  securityPreferencesChangeUsernamePage: {
    title: 'brandedAppConfig.securityPreferencesChangeUsernamePage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.securityPreferencesChangeUsernamePage.Layout.Label',
      dataTestId: 'security-preferences-change-username-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'security-preferences',
          customerId,
        }),
    },
  },
  errorPage: {
    title: 'brandedAppConfig.errorPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'error-page',
    },
  },
  successPage: {
    title: 'brandedAppConfig.successPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'success-page',
    },
  },
  favoriteBeneficiariesPage: {
    title: 'brandedAppConfig.favoriteBeneficiariesPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.favoriteBeneficiariesPage.Layout.Label',
      dataTestId: 'favorite-beneficiaries-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  createFavoriteBeneficiaryPage: {
    title: 'brandedAppConfig.createFavoriteBeneficiaryPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      label: 'brandedAppConfig.createFavoriteBeneficiaryPage.Layout.Label',
      dataTestId: 'create-favorite-beneficiary-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'beneficiaries',
          customerId,
        }),
    },
  },
  authorizationsListPage: {
    title: 'brandedAppConfig.authorizationsListPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.authorizationsListPage.Layout.Label',
      dataTestId: 'authorizations-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  authorizationDetailsPage: {
    title: 'brandedAppConfig.authorizationDetailsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.authorizationDetailsPage.Layout.Label',
      dataTestId: 'authorizations-details-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'authorizations',
          customerId,
        }),
    },
  },
  massPaymentsPage: {
    title: 'brandedAppConfig.massPaymentsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.massPaymentsPage.Layout.Label',
      dataTestId: 'generic-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  payrollPage: {
    title: 'brandedAppConfig.payrollPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.payrollPage.Layout.Label',
      dataTestId: 'payroll-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  payrollDetailsPage: {
    title: 'brandedAppConfig.payrollDetailsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.payroll.Layout.Label',
      dataTestId: 'payroll-details-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'payroll',
          customerId,
        }),
    },
  },
  recurringOrdersPage: {
    title: 'brandedAppConfig.recurringOrdersPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.recurringOrdersPage.Layout.Label',
      dataTestId: 'generic-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  favouriteTransactionsPage: {
    title: 'brandedAppConfig.favouriteTransactionsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.favouriteTransactionsPage.Layout.Label',
      dataTestId: 'favourite-transactions-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  favouriteTransfersPage: {
    title: 'brandedAppConfig.favouriteTransfersPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.favouriteTransfersPage.Layout.Label',
      dataTestId: 'favourite-transfers-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  fundsTransferPage: {
    title: 'brandedAppConfig.fundsTransferPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      label: 'brandedAppConfig.fundsTransferPage.Layout.Label',
      dataTestId: 'fund-transfer-page',
      isFullHeight: true,
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  accountTopupPage: {
    title: 'brandedAppConfig.accountTopupPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      label: 'brandedAppConfig.accountTopupPage.Layout.Label',
      dataTestId: 'account-topup-page',
      isFullHeight: true,
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  cardsListPage: {
    title: 'App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Cards.Text',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Cards.Text',
      dataTestId: 'cards-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  corporateCardsListPage: {
    title: 'App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.CorporateCards.Text',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.CorporateCards.Text',
      dataTestId: 'cards-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  cardDetailsPage: {
    title: 'brandedAppConfig.cardDetailsPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'Card details',
      dataTestId: 'card-details-page',
      backBtnUrl: () => -1,
    },
  },
  topUpCardPage: {
    title: 'brandedAppConfig.topUpCard.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      dataTestId: 'top-up-card-page',
    },
  },
  topUpCardBySelectedAccountPage: {
    title: 'brandedAppConfig.topUpCardBySelectedAccount.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'top-up-card-by-selected-account-page',
    },
  },
  terminateCardPage: {
    title: 'brandedAppConfig.terminateCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'terminate-card-page',
    },
  },
  activateCardPage: {
    title: 'brandedAppConfig.activateCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'activate-card-page',
    },
  },
  replaceCardPage: {
    title: 'brandedAppConfig.replaceCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'replace-card-page',
    },
  },
  permanentBlockCardPage: {
    title: 'brandedAppConfig.permanentBlockCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: false,
      dataTestId: 'permanent-block-card-page',
    },
  },
  exchangeCalculatorPage: {
    title: 'brandedAppConfig.exchangeCalculatorPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.exchangeCalculatorPage.Layout.Label',
      dataTestId: 'exchange-calculator',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  activityLogPage: {
    title: 'brandedAppConfig.activityLog.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.activityLogPage.Layout.Label',
      dataTestId: 'activity-log',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'dashboard',
          customerId,
        }),
    },
  },
  activityLogDetailsPage: {
    title: 'brandedAppConfig.activityLogDetails.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.activityLogDetailsPage.Layout.Label',
      dataTestId: 'activity-log-details',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'activity-log',
          customerId,
        }),
    },
  },
  newPayrollPage: {
    title: 'brandedAppConfig.newPayrollPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.newPayrollPage.Layout.Label',
      dataTestId: 'new-payroll-page',
      backBtnUrl: ({ customerId }: { customerId: string }) =>
        getRoutePath({
          routeKey: 'payroll',
          customerId,
        }),
    },
  },
  issueCardPage: {
    title: 'brandedAppConfig.issueCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      dataTestId: 'issue-card-page',
      label: 'brandedAppConfig.issueCardPage.Layout.Label',
    },
  },
  consentCardPage: {
    title: 'brandedAppConfig.consentCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      dataTestId: 'consent-card-page',
      label: 'brandedAppConfig.consentCardPage.Layout.Label',
    },
  },
  issueCorporateCardPage: {
    title: 'brandedAppConfig.issueCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      dataTestId: 'issue-card-page',
      label: 'brandedAppConfig.issueCardPage.Layout.Label',
    },
  },
  requestCardPage: {
    title: 'brandedAppConfig.issueCardPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      dataTestId: 'issue-card-page',
      label: 'brandedAppConfig.issueCardPage.Layout.Label',
    },
  },
  newIbanAccountPage: {
    title: 'brandedAppConfig.newIbanAccountPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.newIbanAccountPage.Layout.Label',
      dataTestId: 'new-iban-account-page',
      backBtnUrl: () => -1,
    },
  },
  cardUnblockPage: {
    title: 'brandedAppConfig.cardPinUnblockPage.Title',
    layout: {
      type: 'Generic',
      backButton: true,
      label: 'brandedAppConfig.cardPinUnblockPage.Layout.Label',
      dataTestId: 'card-pin-unblock-page',
      backBtnUrl: () => -1,
    },
  },
};

export { brandedAppConfig };
